import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { SavedSettingsStorage } from "../services/savedSettingsStorage";
import { Button, TextInput, useToast } from "@perfectserve/library";
export const PresetsSave = ({ isOpen, onClose, onSave, currentSettingsName, triggerRef }) => {
    const [newSettingsName, setNewSettingsName] = useState("");
    const [error, setError] = useState(null);
    const [position, setPosition] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const { showToast } = useToast();
    useEffect(() => {
        if (isOpen) {
            setError(null);
            setNewSettingsName(currentSettingsName || "");
            setIsClosing(false);
            setShowConfirmation(false);
            if (triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) {
                const rect = triggerRef.current.getBoundingClientRect();
                setPosition({
                    top: rect.bottom + 4,
                    left: rect.left,
                });
            }
            const timer = setTimeout(() => setIsVisible(true), 10);
            return () => clearTimeout(timer);
        }
        else {
            setIsVisible(false);
            const timer = setTimeout(() => setPosition(null), 200);
            return () => clearTimeout(timer);
        }
    }, [isOpen, currentSettingsName, triggerRef]);
    const handleClose = () => {
        setIsClosing(true);
        setIsVisible(false);
        setTimeout(() => {
            onClose();
            setIsClosing(false);
        }, 200);
    };
    const handleSaveNew = () => {
        const name = newSettingsName.trim();
        if (!name) {
            setError("Please enter a name for your preset");
            return;
        }
        if (SavedSettingsStorage.nameExists(name)) {
            setShowConfirmation(true);
            setError(null);
            return;
        }
        savePreset(name);
    };
    const savePreset = (name) => {
        try {
            onSave(name);
            setNewSettingsName("");
            setError(null);
            setShowConfirmation(false);
            showToast({
                message: `Preset "${name}" saved successfully!`,
                type: "info",
                position: "bottom-center",
            });
            handleClose();
        }
        catch (err) {
            showToast({
                message: "Failed to save preset. Please try again.",
                type: "error",
                position: "top-right",
            });
        }
    };
    const handleConfirmUpdate = () => {
        const name = newSettingsName.trim();
        savePreset(name);
    };
    const handleCancelUpdate = () => {
        setShowConfirmation(false);
        setError(null);
    };
    if (!isOpen && !isClosing)
        return null;
    return createPortal(_jsxs(_Fragment, { children: [_jsx("div", { style: {
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 999,
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                    opacity: isVisible ? 1 : 0,
                    transition: "opacity 0.2s ease-out",
                }, onClick: handleClose }), _jsxs("div", { style: {
                    position: "fixed",
                    top: (position === null || position === void 0 ? void 0 : position.top) || 0,
                    left: (position === null || position === void 0 ? void 0 : position.left) || 0,
                    backgroundColor: "white",
                    border: "1px solid #d0d7de",
                    borderRadius: "4px",
                    padding: "20px",
                    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
                    zIndex: 1000,
                    minWidth: "400px",
                    maxWidth: "500px",
                    opacity: isVisible ? 1 : 0,
                    transition: "opacity 0.2s ease-out",
                }, onClick: (e) => e.stopPropagation(), children: [_jsx("div", { style: { marginBottom: "16px" }, children: _jsx("h3", { style: { margin: 0, fontSize: "16px", fontWeight: "600", color: "#24292f" }, children: "Save Current Preset" }) }), error && (_jsx("div", { className: "p-10 mb-15 bg-danger-light text-danger", style: {
                            borderRadius: "var(--ps-border-radius)",
                            fontSize: "14px",
                        }, children: error })), showConfirmation ? (_jsxs("div", { className: "mb-15", children: [_jsx("div", { className: "p-15 mb-15 bg-warning-light text-warning", style: {
                                    borderRadius: "var(--ps-border-radius)",
                                    fontSize: "14px",
                                    border: "1px solid #ffc107",
                                }, children: "Are you sure you want to update this preset?" }), _jsxs("div", { className: "d-flex", style: { gap: "12px", justifyContent: "flex-end" }, children: [_jsx(Button, { onClick: handleCancelUpdate, children: "Cancel" }), _jsx(Button, { onClick: handleConfirmUpdate, variant: "primary", children: "Yes, Update Preset" })] })] })) : (_jsx("div", { className: "mb-15", children: _jsxs("div", { className: "d-flex", style: { gap: "12px" }, children: [_jsx("div", { className: "flex-1", children: _jsx(TextInput, { value: newSettingsName, onChange: (value) => setNewSettingsName(value), placeholder: "Enter a name for your preset...", autoFocus: true }) }), _jsxs(Button, { onClick: handleSaveNew, disabled: !newSettingsName.trim(), variant: "primary", children: [_jsx(FontAwesomeIcon, { icon: faPlus, className: "mr-5" }), "Save"] })] }) })), _jsxs("div", { style: {
                            fontSize: "14px",
                            borderRadius: "var(--ps-border-radius)",
                        }, children: [_jsx("div", { children: "This will save your current selected accounts/users, date range (relative or specific), KPI settings, and View Settings." }), _jsx("div", { className: "mt-10", children: "NOTE: The settings shows all the settings stored on the page and the local cache and these are reset if the cache is cleared." })] }), _jsx("div", { style: { marginTop: "16px", textAlign: "right", borderTop: "1px solid #e5e7eb", paddingTop: "16px" }, children: _jsx(Button, { onClick: handleClose, children: "Cancel" }) })] })] }), document.body);
};
export default PresetsSave;
//# sourceMappingURL=PresetsSave.js.map